import { motion } from "framer-motion";
import { Children, isValidElement, useEffect, useRef, useState } from "react";

/**
 * AnimatedScrollNav
 * - observe l'élément et anime les enfants directs en stagger quand il entre en viewport
 * - relance l'animation quand `watch` change (menuOpen / location.pathname)
 */
const AnimatedScrollNav = ({
  children,
  duration = 0.6,
  watch,
  stagger = 0.06,
  className,
}) => {
  const [token, setToken] = useState(0); // force remount quand watch change
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    setToken((t) => t + 1);
    // reset visible pour relancer l'observation/animation
    setVisible(false);
  }, [watch]);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.unobserve(node);
        }
      },
      { root: null, rootMargin: "0px 0px -80px 0px", threshold: 0.05 }
    );
    obs.observe(node);
    return () => {
      try {
        obs.disconnect();
      } catch (e) {}
    };
  }, [token]);

  const parentVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: stagger } },
  };

  const childVariants = {
    hidden: { opacity: 0, y: 18 },
    visible: { opacity: 1, y: 0, transition: { duration } },
  };

  const raw = Children.toArray(children);

  // Si un seul enfant (ex: <div>...</div>), on clone et on wrappe ses children
  if (raw.length === 1 && isValidElement(raw[0])) {
    const single = raw[0];
    const inner = Children.toArray(single.props.children || []);
    const wrappedInner = inner.map((ch, i) => (
      <motion.div key={i} variants={childVariants} style={{ display: "block" }}>
        {ch}
      </motion.div>
    ));
    const MotionSingle = motion(single.type || "div");
    return (
      <MotionSingle
        key={`${String(watch ?? "static")}-${token}`}
        ref={ref}
        initial="hidden"
        animate={visible ? "visible" : "hidden"}
        variants={parentVariants}
        className={className}
        {...single.props}
      >
        {wrappedInner}
      </MotionSingle>
    );
  }

  // Sinon wrapper standard
  return (
    <motion.div
      key={`${String(watch ?? "static")}-${token}`}
      ref={ref}
      initial="hidden"
      animate={visible ? "visible" : "hidden"}
      variants={parentVariants}
      className={className}
      style={{ willChange: "transform, opacity" }}
    >
      {raw.map((ch, i) => (
        <motion.div key={i} variants={childVariants}>
          {ch}
        </motion.div>
      ))}
    </motion.div>
  );
};

export default AnimatedScrollNav;
